"use client";
// Imports
import Link from "next/link";
import { Container, ComponentWrapper, Img } from "@/components";
import { cva } from "class-variance-authority";
import { cn, resolveLink } from "@/lib/utils";
import { Button } from "@/components/ui/button";

function backgroundColor(tile, overrideTheme, theme) {
  if (overrideTheme && tile?.background_tile_color) {
    return tile?.background_tile_color;
  } else {
    return theme?.background_color;
  }
}

function foregroundColor(tile, overrideTheme, theme) {
  if (overrideTheme && tile?.foreground_tile_color) {
    return tile?.foreground_tile_color;
  } else {
    return theme?.foregorund_color;
  }
}

const PRIMARY_BUTTON_COLORS = {
  background: "#892102", // red gum
  foreground: "#FFFFFF",
};

function tileButtonVariant(tile) {
  const style =
    typeof tile?.button_style === "object"
      ? tile?.button_style?.value
      : tile?.button_style;
  return style === "outline" ? "outline" : "default";
}

function tileButtonCustomColors(tile, overrideTheme, theme) {
  if (tile?.use_custom_colours) {
    return {
      background: tile?.custom_button_background_color,
      foreground: tile?.custom_button_foreground_color,
    };
  }
  // Legacy: inverted colours when override theme is enabled (no explicit custom colours)
  if (overrideTheme && tile?.foreground_tile_color) {
    return {
      background: tile?.foreground_tile_color,
      foreground:
        tile?.background_tile_color || theme?.button_foreground_color,
    };
  }
  // Default filled buttons use 2026 primary (red gum), not theme orange
  if (tileButtonVariant(tile) === "default") {
    return PRIMARY_BUTTON_COLORS;
  }
  return undefined;
}

const tileStyles = cva("flex flex-col relative overflow-hidden rounded-lg", {
  variants: {
    width: {
      0: "col-span-12 sm:col-span-6 lg:col-span-3 lg:max-h-none",
      1: "col-span-12 lg:col-span-4 lg:max-h-none",
      2: "col-span-12 lg:col-span-8 lg:max-h-none",
      3: "col-span-12 lg:col-span-6 lg:max-h-none",
      4: "col-span-12 lg:col-span-12 flex lg:flex-row flex-col overflow-hidden max-h-[500px] lg:max-h-none",
    },
  },
});

// Tiles
export function Tiles({ type, tiles, overrideTheme, theme }) {
  // Render
  return (
    <ComponentWrapper type={type}>
      <Container>
        <div className="grid grid-cols-12 gap-6 w-full lg:gap-12">
          {tiles.map((tile, index) => {
            switch (tile.type) {
              case "1_4_tile":
                return (
                  <TileOneWide
                    key={index}
                    index={index}
                    tile={tile}
                    theme={theme}
                    overrideTheme={overrideTheme}
                    width={0}
                  />
                );
              case "1_3_tile":
                return (
                  <TileOneWide
                    key={index}
                    index={index}
                    tile={tile}
                    theme={theme}
                    overrideTheme={overrideTheme}
                  />
                );
              case "2_3_tile":
                return (
                  <TileTwoWide
                    key={index}
                    index={index}
                    tile={tile}
                    theme={theme}
                    overrideTheme={overrideTheme}
                  />
                );
              case "1_2_tile":
                return (
                  <TileHalfWide
                    key={index}
                    index={index}
                    tile={tile}
                    theme={theme}
                    overrideTheme={overrideTheme}
                  />
                );
              case "full_tile":
                return (
                  <TileThreeWide
                    key={index}
                    index={index}
                    tile={tile}
                    theme={theme}
                    overrideTheme={overrideTheme}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      </Container>
    </ComponentWrapper>
  );
}

// Tile One Wide
function TileOneWide({ tile, overrideTheme, theme, width = 1 }) {
  const link = resolveLink(tile?.link);
  const isExternalLink = link?.includes("http");

  if (isExternalLink) {
    return (
      <a
        href={link ?? "#"}
        style={{
          backgroundColor: backgroundColor(tile, overrideTheme, theme),
          color: foregroundColor(tile, overrideTheme, theme),
        }}
        className={cn(
          tileStyles({
            width,
          }),
          link ? "cursor-pointer" : "pointer-events-none"
        )}
      >
        <div className="relative h-[200px] sm:h-[300px] lg:h-[400px]">
          <Img
            data={tile.image}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
          />
        </div>
        {titleContent(tile, overrideTheme, theme)}
      </a>
    );
  }

  // Render
  return (
    <Link
      href={link ?? "#"}
      style={{
        backgroundColor: backgroundColor(tile, overrideTheme, theme),
        color: foregroundColor(tile, overrideTheme, theme),
      }}
      className={cn(
        tileStyles({
          width,
        }),
        link ? "cursor-pointer" : "pointer-events-none"
      )}
    >
      <div className="relative h-[200px] sm:h-[300px] lg:h-[400px]">
        <Img
          data={tile.image}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
        />
      </div>
      {titleContent(tile, overrideTheme, theme)}
    </Link>
  );
}

// Tile Two Wide
function TileTwoWide({ tile, overrideTheme, theme }) {
  const link = resolveLink(tile?.link);
  const isExternalLink = link?.includes("http");

  if (isExternalLink) {
    return (
      <a
        href={link ?? "#"}
        style={{
          backgroundColor: backgroundColor(tile, overrideTheme, theme),
          color: foregroundColor(tile, overrideTheme, theme),
        }}
        className={cn(
          tileStyles({
            width: 2,
          }),
          link ? "cursor-pointer" : "pointer-events-none"
        )}
      >
        {titleContent(tile, overrideTheme, theme)}
      </a>
    );
  }
  // Render
  return (
    <Link
      href={link ?? "#"}
      style={{
        backgroundColor: backgroundColor(tile, overrideTheme, theme),
        color: foregroundColor(tile, overrideTheme, theme),
      }}
      className={cn(
        tileStyles({
          width: 2,
        }),
        link ? "cursor-pointer" : "pointer-events-none"
      )}
    >
      <div className="relative h-[200px] sm:h-[300px] lg:h-[400px]">
        <Img data={tile.image} fill sizes="(max-width: 1024px) 100vw, 1000px" />
      </div>
      {titleContent(tile, overrideTheme, theme)}
    </Link>
  );
}

// Tile Three Wide
function TileThreeWide({ tile, overrideTheme, theme }) {
  const link = resolveLink(tile?.link);
  const isExternalLink = link?.includes("http");

  if (isExternalLink) {
    return (
      <a
        href={link ?? "#"}
        style={{
          backgroundColor: backgroundColor(tile, overrideTheme, theme),
          color: foregroundColor(tile, overrideTheme, theme),
        }}
        className={cn(
          tileStyles({
            width: 4,
          }),
          link ? "cursor-pointer" : "pointer-events-none"
        )}
      >
        {titleContent(tile, overrideTheme, theme)}
      </a>
    );
  }

  // Render
  return (
    <Link
      href={link ?? "#"}
      style={{
        backgroundColor: backgroundColor(tile, overrideTheme, theme),
        color: foregroundColor(tile, overrideTheme, theme),
      }}
      className={cn(
        tileStyles({
          width: 4,
        }),
        link ? "cursor-pointer" : "pointer-events-none"
      )}
    >
      <div className="w-full lg:w-[49%]">
        {titleContent(tile, overrideTheme, theme)}
      </div>
      <div className="h-[200px] sm:h-[300px] lg:h-[550px] w-full order-first lg:order-last relative">
        <Img fill data={tile.image} />
      </div>
    </Link>
  );
}

// Tile Half Wide
function TileHalfWide({ tile, overrideTheme, theme }) {
  const link = resolveLink(tile?.link);
  const isExternalLink = link?.includes("http");

  if (isExternalLink) {
    return (
      <a
        href={link ?? "#"}
        style={{
          backgroundColor: backgroundColor(tile, overrideTheme, theme),
          color: foregroundColor(tile, overrideTheme, theme),
        }}
        className={cn(
          tileStyles({
            width: 3,
          }),
          link ? "cursor-pointer" : "pointer-events-none"
        )}
      >
        <div className="relative h-[200px] sm:h-[300px] lg:h-[400px]">
          <Img data={tile.image} fill sizes="(max-width: 1024px) 100vw, 1000px" />
        </div>
        {titleContent(tile, overrideTheme, theme)}
      </a>
    );
  }
  // Render
  return (
    <Link
      href={link ?? "#"}
      style={{
        backgroundColor: backgroundColor(tile, overrideTheme, theme),
        color: foregroundColor(tile, overrideTheme, theme),
      }}
      className={cn(
        tileStyles({
          width: 3,
        }),
        link ? "cursor-pointer" : "pointer-events-none"
      )}
    >
      <div className="relative h-[200px] sm:h-[300px] lg:h-[400px]">
        <Img data={tile.image} fill sizes="(max-width: 1024px) 100vw, 1000px" />
      </div>
      {titleContent(tile, overrideTheme, theme)}
    </Link>
  );
}

function titleContent(tile, overrideTheme, theme) {
  return (
    <div className="flex flex-col flex-1 h-full opacity-100 relative z-1 p-4 lg:p-6 bg-white">
      <div className="flex flex-col space-y-2">
        <h2 className="level-2 text-pretty">{tile.title}</h2>
        {tile.subtitle && (
          <p className="level-4 text-pretty">{tile.subtitle}</p>
        )}
        {tile.description && (
          <p className="text-base leading-tight text-pretty">
            {tile.description}
          </p>
        )}
      </div>
      <div className="pt-4 mt-auto">
        {tile?.show_info ? (
          <p className="text-base">{tile?.info}</p>
        ) : (
          <Button
            size="sm"
            variant={tileButtonVariant(tile)}
            customColors={tileButtonCustomColors(tile, overrideTheme, theme)}
            className={!tile.button_label ? "hidden" : undefined}
          >
            {tile.button_label}
          </Button>
        )}
      </div>
    </div>
  );
}
